import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaBalanceScale, FaShieldAlt, FaUsers } from 'react-icons/fa';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';

const BlockchainFundPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Blockchain Fund' }
  ];

  const keyFeatures = [
    {
      icon: <FaChartLine className="w-10 h-10 text-secondary" />,
      title: "High Growth Potential",
      description: "Our blockchain fund targets high-growth blockchain projects with disruptive potential in various industries, aiming for above-average returns."
    },
    {
      icon: <FaBalanceScale className="w-10 h-10 text-secondary" />,
      title: "Risk Management",
      description: "We employ sophisticated risk management strategies to mitigate volatility while maintaining exposure to the upside potential of blockchain investments."
    },
    {
      icon: <FaShieldAlt className="w-10 h-10 text-secondary" />,
      title: "Security-First Approach",
      description: "Our team conducts rigorous security audits and due diligence on all potential investments, prioritizing projects with strong security practices."
    },
    {
      icon: <FaUsers className="w-10 h-10 text-secondary" />,
      title: "Expert Team",
      description: "Our fund is managed by a diverse team of experts with backgrounds in blockchain technology, traditional finance, cryptography, and venture capital."
    }
  ];

  const stats = [
    { value: "$225M", label: "Assets Under Management" },
    { value: "35+", label: "Blockchain Projects" },
    { value: "27%", label: "Average Annual Return" },
    { value: "2018", label: "Fund Established" }
  ];

  return (
    <>
      <PageHeader 
        title="Blockchain Fund" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/blockchain-background.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">Investing in the Future of Blockchain Technology</h2>

              <p className="text-lg text-gray-700">
                The Blockfirst Capital Blockchain Fund provides investors with access to a carefully curated portfolio of blockchain projects, protocols, and companies that are building the infrastructure of the digital economy.
              </p>

              <p className="text-lg text-gray-700">
                Our experienced team identifies early-stage and growth-stage opportunities across the blockchain ecosystem, focusing on projects with strong fundamentals, innovative technology, and significant market potential.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm">
                    <div className="text-3xl font-heading font-bold text-secondary mb-2">{stat.value}</div>
                    <div className="text-gray-600 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/blockchain-investment.jpg" 
                alt="Blockchain Fund" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold mb-6">Fund Features</h2>
            <p className="text-lg text-gray-300">
              Our Blockchain Fund is designed to provide investors with comprehensive exposure to the blockchain ecosystem while prioritizing risk management and long-term growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-white/15 transition-all duration-300"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-heading font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/blockchain-investment-strategy.jpg" 
                alt="Investment Strategy" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">Investment Strategy</h2>

              <p className="text-lg text-gray-700">
                Our investment strategy is built on thorough research, technical analysis, and strategic partnerships within the blockchain ecosystem. We focus on identifying projects that demonstrate:
              </p>

              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Strong Fundamentals</h3>
                  <p className="text-gray-600">
                    We evaluate tokenomics, team expertise, market opportunity, and competitive advantages to identify projects with solid fundamentals.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Technical Innovation</h3>
                  <p className="text-gray-600">
                    We prioritize projects that solve real-world problems through innovative blockchain applications and technologies.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Growth Potential</h3>
                  <p className="text-gray-600">
                    We analyze market trends, adoption metrics, and partnership opportunities to identify projects with significant growth potential.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">How to Invest</h2>
            <p className="text-lg text-gray-600">
              Investing in our Blockchain Fund is a straightforward process designed to provide you with full transparency and confidence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Schedule a Consultation</h3>
              <p className="text-gray-600">
                Contact our team to schedule a personalized consultation with one of our investment advisors to discuss your financial goals.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Review Fund Documents</h3>
              <p className="text-gray-600">
                Our team will provide you with detailed fund documents, including the investment strategy, risk factors, and performance history.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Complete Subscription</h3>
              <p className="text-gray-600">
                Once you're ready to invest, complete the subscription agreement and transfer your investment to begin your blockchain investment journey.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link 
              to="/contact-us" 
              className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
            >
              Request Investment Information
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">Our Blockchain Portfolio</h2>
            <p className="text-lg text-gray-600">
              Explore our specialized investment products within the blockchain ecosystem, each designed to target specific opportunities and strategies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom overflow-hidden"
            >
              <div className="h-48 bg-primary/10 flex items-center justify-center">
                <img src="/assets/liquid-token.svg" alt="Liquid Token" className="w-24 h-24 object-contain" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold mb-3 text-primary">Liquid Token</h3>
                <p className="text-gray-600 mb-4">
                  Investment in established, high-liquidity cryptocurrencies with strong market presence.
                </p>
                <Link to="/portfolio/liquid-token" className="text-secondary hover:text-secondary-dark font-medium inline-flex items-center">
                  Learn More 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom overflow-hidden"
            >
              <div className="h-48 bg-primary/10 flex items-center justify-center">
                <img src="/assets/bitcoin-trading.svg" alt="Bitcoin Trading Fund" className="w-24 h-24 object-contain" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold mb-3 text-primary">Bitcoin Trading Fund</h3>
                <p className="text-gray-600 mb-4">
                  Active trading strategies focused on Bitcoin to capitalize on market volatility.
                </p>
                <Link to="/portfolio/bitcoin-trading" className="text-secondary hover:text-secondary-dark font-medium inline-flex items-center">
                  Learn More 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom overflow-hidden"
            >
              <div className="h-48 bg-primary/10 flex items-center justify-center">
                <img src="/assets/early-stage-token.svg" alt="Early-Stage Token Fund" className="w-24 h-24 object-contain" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold mb-3 text-primary">Early-Stage Token Fund</h3>
                <p className="text-gray-600 mb-4">
                  Investments in promising early-stage tokens with significant growth potential.
                </p>
                <Link to="/portfolio/early-stage-token" className="text-secondary hover:text-secondary-dark font-medium inline-flex items-center">
                  Learn More 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom overflow-hidden"
            >
              <div className="h-48 bg-primary/10 flex items-center justify-center">
                <img src="/assets/staking-fund.svg" alt="Staking Fund" className="w-24 h-24 object-contain" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold mb-3 text-primary">Staking Fund</h3>
                <p className="text-gray-600 mb-4">
                  Generate passive income through staking and yield farming in proof-of-stake blockchains.
                </p>
                <Link to="/portfolio/staking-fund" className="text-secondary hover:text-secondary-dark font-medium inline-flex items-center">
                  Learn More 
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlockchainFundPage; 